/*
 * gifted. — custom phrase  ·  PHASE 3
 * PATH: assets/gifted-custom-phrase.js
 * Replaces the phase 2 file entirely. 17 Aug 2026.
 *
 * WHAT PHASE 3 ADDS
 *   The fit verdict. The block now counts the beads a phrase actually costs and
 *   says whether that fits the size she picked — in the unit the brand already
 *   publishes, which is beads, not characters.
 *
 * THREE CORRECTIONS TO THE PHASE 2 ENCODER, ALL FOUNDER-RULED 17 AUG 2026
 *   1. A spacer follows EVERY letter, including the last one in the phrase.
 *      Phase 2 only put spacers BETWEEN letters, so every phrase was one bead
 *      short.
 *   2. Digits carry no spacer of their own. A run of digits is five beads per
 *      digit, run together, with one spacer closing the group. Phase 2 put a
 *      spacer inside "17", which is a bead that would have been strung.
 *   3. The word break IS the letter spacer. A word space costs nothing extra.
 *      Phase 2 already had this right and it is kept.
 *
 * WHY BEADS AND NOT CHARACTERS
 *   The Brand Identity Document, section 12: "There is no character count that
 *   is correct for any band." HELLO and MOON are both short words with very
 *   different physical runs. Capacity is published in beads because beads are
 *   what the wrist has room for.
 *
 * WHAT IT WILL NOT DO
 *   It will not tell her to size up. The brand rule is that the wrist picks the
 *   band and the message fits inside it — shortening the phrase is the only
 *   remedy the system has, and copy implying otherwise is wrong.
 *   It will not block add to cart. It warns, and she decides.
 *
 * FAILURE DESIGN, UNCHANGED SINCE PHASE 1
 *   The phrase, the dot/dash preference and the mock-up request are plain form
 *   inputs. They reach the order with no JavaScript at all. If this file fails
 *   to load or parse, the customer loses the drawing, the readout and the
 *   verdict. Nothing she typed is dropped.
 *
 * DEGRADES, NEVER BLANKS
 *   Phase 2 erased the drawing whenever it could not work out a circumference.
 *   Phase 3 draws at a guide scale and says so.
 */
 
if (!customElements.get('gifted-custom-phrase')) {
  const MORSE = {
    A: '.-', B: '-...', C: '-.-.', D: '-..', E: '.', F: '..-.', G: '--.', H: '....',
    I: '..', J: '.---', K: '-.-', L: '.-..', M: '--', N: '-.', O: '---', P: '.--.',
    Q: '--.-', R: '.-.', S: '...', T: '-', U: '..-', V: '...-', W: '.--', X: '-..-',
    Y: '-.--', Z: '--..',
    0: '-----', 1: '.----', 2: '..---', 3: '...--', 4: '....-', 5: '.....',
    6: '-....', 7: '--...', 8: '---..', 9: '----.',
    '?': '..--..', '!': '-.-.--', "'": '.----.', '=': '-...-', ';': '-.-.-.',
    '"': '.-..-.', '+': '.-.-.', '&': '.-...', '/': '-..-.', '.': '.-.-.-',
    ',': '--..--', ':': '---...',
  };
 
  /* The palette is derived from the option value itself. Every colour is named
     "Something | Base", and the Base word is real data the merchant already
     maintains. One small table therefore covers all 54 today and Anchor's 246
     later, with no list to keep in sync. Exact shades can be overridden per
     colour in the block settings. */
  const BASE = {
    red: '#C0392B', pink: '#E6A6B8', orange: '#E8720C', yellow: '#D9D42B',
    green: '#3E7A4E', teal: '#3E8C8C', blue: '#2F5FA8', purple: '#6E4B8E',
    gray: '#8E8C90', grey: '#8E8C90', black: '#1E1B1A', white: '#F2EFEA',
    beige: '#D9C7A9', brown: '#7A5C3E', gold: '#C9A227', silver: '#B9BCC0',
  };
 
  const METAL = {
    gold: { hi: '#F6E3A8', mid: '#C9A227', lo: '#7A5F12' },
    silver: { hi: '#F2F4F6', mid: '#B9BCC0', lo: '#6E7276' },
  };
 
  const DIGIT = /[0-9]/;
 
  /* Settings arrive as strings because a merchant types them. Never trust one
     into arithmetic — phase 2 interpolated them raw into JSON, so one stray
     "mm" would have broken the whole config object silently. */
  const num = (v, fallback) => {
    const n = parseFloat(v);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };
 
  class GiftedCustomPhrase extends HTMLElement {
    #controller = null;
    #cfg = null;
    #input = null;
    #morse = null;
    #readout = null;
    #stage = null;
    #balance = null;
    #fit = null;
    #scale = null;
    #selected = [];
 
    connectedCallback() {
      this.#cfg = this.#readConfig();
      this.#input = this.querySelector('[data-gifted-phrase-input]');
      this.#morse = this.querySelector('[data-gifted-phrase-morse]');
      this.#readout = this.querySelector('[data-gifted-phrase-readout]');
      this.#stage = this.querySelector('[data-gifted-phrase-stage]');
      this.#balance = this.querySelector('[data-gifted-phrase-balance]');
      this.#fit = this.querySelector('[data-gifted-phrase-fit]');
      this.#scale = this.querySelector('[data-gifted-phrase-scale]');
      if (!this.#input) return;
 
      this.#selected = this.#readSelection(this.#cfg?.selected, this.#cfg?.variantTitle);
      this.#repairFormAssociation();
 
      this.#controller = new AbortController();
      const sig = { signal: this.#controller.signal };
 
      this.#input.addEventListener('input', this.#update, sig);
      this.#balance?.addEventListener('change', this.#update, sig);
 
      this.closest('.shopify-section, dialog')
        ?.addEventListener('shopify:product:select', this.#onSelect, sig);
 
      this.#update();
    }
 
    disconnectedCallback() {
      this.#controller?.abort();
      this.#controller = null;
    }
 
    #readConfig() {
      try {
        const el = this.querySelector('[data-gifted-phrase-config]');
        return el ? JSON.parse(el.textContent) : null;
      } catch {
        return null;                       // any problem disables the extras only
      }
    }
 
    /** Option values as plain strings, from whichever source has them.
        A variant title is the option values joined with " / ", so it is a
        complete fallback when the array is empty. */
    #readSelection(list, title) {
      const clean = (Array.isArray(list) ? list : [])
        .filter((x) => typeof x === 'string' && x.trim())
        .map((x) => x.trim());
      if (clean.length) return clean;
      if (typeof title === 'string' && title.trim() && title !== 'Default Title') {
        return title.split('/').map((s) => s.trim()).filter(Boolean);
      }
      return [];
    }
 
    /** Atelier's own components read event.detail and event.promise from this
        event and never touch event.selectedOptions, so do not depend on one
        shape. Anything that fails to yield option values leaves the previous
        selection standing rather than wiping it. */
    #onSelect = (event) => {
      const next = this.#readSelection(
        event?.selectedOptions
          || event?.detail?.selectedOptions
          || event?.detail?.resource?.options,
        event?.detail?.resource?.title,
      );
      if (next.length) this.#selected = next;
      this.#update();
 
      event?.promise?.then?.(({ detail }) => {
        const later = this.#readSelection(detail?.resource?.options, detail?.resource?.title);
        if (later.length) { this.#selected = later; this.#update(); }
      }).catch(() => {});
    };
 
    /* --- reading the customer's choices out of the option values --- */
 
    #colour() {
      const override = this.#cfg?.overrides || {};
      for (const v of this.#selected) {
        if (override[v]) return override[v];
      }
      for (const v of this.#selected) {
        if (!String(v).includes('|')) continue;             // colours look like "Name | Base"
        const base = String(v).split('|').pop().trim().toLowerCase();
        if (BASE[base]) return BASE[base];
      }
      return this.#cfg?.fallbackColour || BASE.teal;
    }
 
    #metal() {
      const joined = this.#selected.join(' ').toLowerCase();
      if (/\bsilver\b|sterling|925/.test(joined)) return 'silver';
      return 'gold';                                         // gold fill is the house default
    }
 
    /** Match a lookup table against the selected option values.
        LONGEST KEY FIRST, deliberately. "Standard" is a substring of
        "Standard neck", and matching shortest-first turned a 16 inch necklace
        into a 6 inch bracelet. Phase 2 matched in object order and got away
        with it only because the necklace template does not carry this block. */
    #lookup(table) {
      if (!table) return null;
      const keys = Object.keys(table).sort((a, b) => b.length - a.length);
      for (const v of this.#selected) {
        const key = String(v).trim();
        if (table[key] != null) return { name: key, value: table[key] };
        const lower = key.toLowerCase();
        for (const name of keys) {
          if (lower.includes(name.toLowerCase())) return { name, value: table[name] };
        }
      }
      return null;
    }
 
    /** Finished circumference in mm, read from the size option. Null when
        unknown — and when it is null the drawing falls back to a guide scale
        and says so rather than disappearing. */
    #circumference() {
      const hit = this.#lookup(this.#cfg?.ladder);
      if (hit) return num(hit.value, 0) * 25.4 || null;
      for (const v of this.#selected) {
        const inches = String(v).match(/(\d+(?:\.\d+)?)\s*(?:in\b|inch)/i);
        if (inches) return parseFloat(inches[1]) * 25.4;
      }
      return null;
    }
 
    /* --- encoding ---
       Founder ruling, 17 Aug 2026, and Brand Identity Document section 2C:
         · a 0.8mm spacer follows EVERY letter, number group and symbol
         · a digit is five beads flat and carries NO spacer of its own
         · inside a digit group the spacer falls at the group boundary only
         · the word break is that same spacer, so a space costs nothing extra */
 
    #chooseGlass(text, preference) {
      let d = 0, dash = 0;
      String(text).toUpperCase().split(/\s+/).forEach((w) =>
        [...w].forEach((c) => {
          if (MORSE[c]) [...MORSE[c]].forEach((s) => (s === '.' ? d++ : dash++));
        }));
      const majority = d >= dash ? 'dot' : 'dash';
      return preference === 'More metal' ? (majority === 'dot' ? 'dash' : 'dot') : majority;
    }
 
    #encode(text, glassOn) {
      const g = this.#cfg?.geometry || {};
      const wDot = num(g.dot, 1.7), wDash = num(g.dash, 2.5), wGap = num(g.spacer, 0.8);
 
      /* Fold accents before encoding. Phase 2 dropped them silently, so "café"
         became CAF and "Zoë" became ZO — a letter missing from a finished piece
         with nothing on the page to say so. */
      const clean = String(text)
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toUpperCase().trim();
 
      const dropped = [...new Set([...clean].filter((c) => !MORSE[c] && !/\s/.test(c)))];
      const tokens = clean.split(/\s+/).filter(Boolean);
      const run = [], lines = [];
 
      const signal = (sym) => {
        const type = sym === '.' ? 'dot' : 'dash';
        const mat = type === glassOn ? 'glass' : 'metal';
        run.push({ mat, w: mat === 'glass' ? wDot : wDash });
      };
      const emit = (c) => { [...MORSE[c]].forEach(signal); lines.push(MORSE[c]); };
      const spacer = () => run.push({ mat: 'spacer', w: wGap });
 
      tokens.forEach((token, ti) => {
        const chars = [...token].filter((c) => MORSE[c]);
        let i = 0;
        while (i < chars.length) {
          if (DIGIT.test(chars[i])) {
            while (i < chars.length && DIGIT.test(chars[i])) emit(chars[i++]);
            spacer();                       // one spacer closes the whole group
          } else {
            emit(chars[i++]);
            spacer();                       // a spacer follows every letter
          }
        }
        if (ti < tokens.length - 1 && chars.length) lines.push('/');
      });
 
      const mm = run.reduce((s, b) => s + b.w, 0);
      return { run, morse: lines.join(' '), beads: run.length, mm, dropped };
    }
 
    /* --- the verdict --- */
 
    /** Capacity for the size she has picked.
        Published bead capacity wins over anything derived. Those figures are in
        the brand document, they already contain the spacers, and they were set
        conservatively — recomputing them from millimetres would only invent a
        second number that disagrees with the printed card. */
    #capacity() {
      const cap = this.#cfg?.capacity;
      const band = this.#lookup(this.#cfg?.ladder);
 
      /* A published capacity is only claimed for a band the ladder ALSO names.
         Without this guard "Standard neck" collects "Standard"'s bracelet
         capacity of 75 beads, because one is a substring of the other. */
      if (cap) {
        if (band) {
          if (cap[band.name] != null) {
            const beads = num(cap[band.name], 0);
            if (beads) return { beads, label: band.name, source: 'published' };
          }
        } else {
          const hit = this.#lookup(cap);
          if (hit) {
            const beads = num(hit.value, 0);
            if (beads) return { beads, label: hit.name, source: 'published' };
          }
        }
      }
 
      const circ = this.#circumference();
      if (!circ) return null;
      const mm = circ
        - num(this.#cfg?.findings, 15)      // crimp covers, jump ring, spring ring
        - num(this.#cfg?.charmDrop, 0);     // hangs off the end on a charmed piece
      if (mm <= 0) return null;
      const inches = circ / 25.4;
      const label = this.#lookup(this.#cfg?.ladder)?.name
        || `${Number(inches.toFixed(2))} inches`;
      return { mm, label, source: 'derived' };
    }
 
    #fill(template, tokens) {
      return String(template || '').replace(/\{(\w+)\}/g, (m, k) =>
        (tokens[k] === undefined ? m : tokens[k]));
    }
 
    #verdict(used) {
      const copy = this.#cfg?.copy || {};
      const cap = this.#capacity();
 
      if (!cap) {
        return { state: 'unknown', text: this.#fill(copy.unknown, { beads: used.beads }) };
      }
 
      const ratio = cap.beads ? used.beads / cap.beads : used.mm / cap.mm;
      const perBead = used.beads ? used.mm / used.beads : 1.7;
      const over = cap.beads
        ? Math.max(1, used.beads - cap.beads)
        : Math.max(1, Math.ceil((used.mm - cap.mm) / perBead));
 
      const tokens = {
        beads: used.beads,
        capacity: cap.beads || Math.floor(cap.mm / perBead),
        size: cap.label,
        over: `${over} ${over === 1 ? 'bead' : 'beads'}`,
      };
 
      if (ratio > 1) return { state: 'over', text: this.#fill(copy.over, tokens) };
      if (ratio > 0.92) return { state: 'tight', text: this.#fill(copy.tight, tokens) };
      return { state: 'ok', text: this.#fill(copy.ok, tokens) };
    }
 
    /* --- the drawing --- */
 
    #shade(hex, amt) {
      const n = parseInt(String(hex).slice(1), 16);
      if (Number.isNaN(n)) return hex;
      const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255]
        .map((v) => Math.max(0, Math.min(255, Math.round(v + amt))));
      return '#' + c.map((v) => v.toString(16).padStart(2, '0')).join('');
    }
 
    #draw(run, circumference, toScale) {
      if (!this.#stage) return;
      if (!run.length) { this.#stage.innerHTML = ''; return; }
 
      const bead = this.#colour();
      const m = METAL[this.#metal()];
      const r = circumference / (2 * Math.PI);
      const pad = 11;
      const uid = this.#cfg?.uid || 'x';
 
      const used = run.reduce((s, b) => s + b.w, 0);
      let arc = -used / 2;                                    // centred opposite the clasp
      const parts = [];
 
      parts.push(`<defs>
        <radialGradient id="gm-${uid}" cx="34%" cy="30%" r="72%">
          <stop offset="0%" stop-color="${m.hi}"/>
          <stop offset="46%" stop-color="${m.mid}"/>
          <stop offset="100%" stop-color="${m.lo}"/>
        </radialGradient>
        <linearGradient id="gb-${uid}" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="${this.#shade(bead, 48)}"/>
          <stop offset="42%" stop-color="${bead}"/>
          <stop offset="100%" stop-color="${this.#shade(bead, -42)}"/>
        </linearGradient>
        <filter id="gs-${uid}" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy=".35" stdDeviation=".4" flood-color="#000" flood-opacity=".45"/>
        </filter>
      </defs>`);
 
      parts.push(`<circle cx="0" cy="0" r="${r}" fill="none" stroke="${m.mid}" stroke-width="1.05" opacity="${toScale ? '.9' : '.45'}"/>`);
 
      run.forEach((b) => {
        const deg = ((arc + b.w / 2) / circumference) * 360;
        const t = `rotate(${deg.toFixed(3)}) translate(0,${-r})`;
        if (b.mat === 'metal') {
          parts.push(`<g transform="${t}" filter="url(#gs-${uid})"><circle r="1.25" fill="url(#gm-${uid})"/><ellipse cx="-.34" cy="-.4" rx=".42" ry=".3" fill="#fff" opacity=".5"/></g>`);
        } else if (b.mat === 'glass') {
          parts.push(`<g transform="${t}" filter="url(#gs-${uid})"><rect x="${-b.w / 2}" y="-1.07" width="${b.w}" height="2.15" rx=".28" fill="url(#gb-${uid})"/></g>`);
        } else {
          parts.push(`<g transform="${t}"><rect x="${-b.w / 2}" y="-.82" width="${b.w}" height="1.64" rx=".2" fill="url(#gm-${uid})"/></g>`);
        }
        arc += b.w;
      });
 
      parts.push(`<g transform="translate(0,${r})"><circle cx="0" cy="0" r="2.5" fill="none" stroke="url(#gm-${uid})" stroke-width="1.15"/></g>`);
 
      this.#stage.setAttribute('viewBox', `${-r - pad} ${-r - pad} ${(r + pad) * 2} ${(r + pad) * 2}`);
      this.#stage.innerHTML = parts.join('');
    }
 
    /* --- form association, unchanged from phase 1 --- */
 
    #repairFormAssociation() {
      const wanted = this.dataset.formId;
      if (wanted && document.getElementById(wanted)) return;
      const scope = this.closest('.shopify-section, dialog') || document;
      const form = scope.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) return;
      if (!form.id) form.id = `gifted-phrase-form-${Math.random().toString(36).slice(2, 9)}`;
      this.querySelectorAll('[form]').forEach((el) => el.setAttribute('form', form.id));
    }
 
    #update = () => {
      const glassOn = this.#chooseGlass(this.#input.value, this.#balance?.value);
      const used = this.#encode(this.#input.value, glassOn);
 
      if (this.#morse) this.#morse.value = used.morse;
      if (this.#readout) this.#readout.textContent = used.morse || '';
 
      /* The drawing. A real circumference draws to scale; without one we draw
         the same strand at a guide scale rather than erasing it. */
      const circ = this.#circumference();
      const toScale = Boolean(circ);
      const nominal = Math.max(used.mm * 1.35, 120);
      this.#draw(used.run, toScale ? circ : nominal, toScale);
 
      if (this.#scale) {
        this.#scale.textContent = (used.beads && !toScale) ? (this.#cfg?.copy?.guideScale || '') : '';
      }
 
      if (this.#fit) {
        if (!used.beads) {
          this.#fit.textContent = '';
          this.#fit.removeAttribute('data-state');
        } else {
          const v = this.#verdict(used);
          const note = used.dropped.length
            ? ' ' + this.#fill(this.#cfg?.copy?.dropped, { chars: used.dropped.join(' ') })
            : '';
          this.#fit.textContent = (v.text + note).trim();
          this.#fit.setAttribute('data-state', v.state);
        }
      }
    };
  }
 
  customElements.define('gifted-custom-phrase', GiftedCustomPhrase);
}